import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
interface ImageCarouselProps {
  images: string[];
  projectTitle?: string;
}
const ImageCarousel = ({
  images,
  projectTitle = 'Digitale Produktionssteuerung mit Planungstool'
}: ImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  // Bestimme die Hintergrundfarbe basierend auf dem Projekttitel
  let backgroundColor = 'white';
  if (projectTitle === 'Projektmanagement-Tool') {
    backgroundColor = '#1a2332';
  } else if (projectTitle === 'Optimierte Netzauslastung durch smarte Energieverteilung') {
    backgroundColor = '#3e3d3b';
  } else if (projectTitle === 'Digitale Produktionssteuerung mit Planungstool') {
    backgroundColor = '#f9fafb';
  } else if (projectTitle === 'Zuverlässige Maschinendatenerfassung für Langzeitanalysen') {
    backgroundColor = '#1a2332';
  } else if (projectTitle === 'Chargenrückverfolgung und stabile Produktionsprozesse') {
    backgroundColor = '#f3f4f6';
  }
  // Prüfe, ob ein Smartphone-Rahmen angezeigt werden soll
  const shouldShowSmartphoneFrame = () => {
    // Für Projekt 4 (Chargenrückverfolgung) keinen Smartphone-Rahmen mehr anzeigen
    if (projectTitle === 'Chargenrückverfolgung und stabile Produktionsprozesse') {
      return false;
    }
    // Für andere Projekte bleibt die Logik unverändert
    return false;
  };
  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };
  const goToNext = () => {
    const isLastSlide = currentIndex === images.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };
  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };
  const openLightbox = () => {
    setIsLightboxOpen(true);
    // Scroll verhindern, wenn Lightbox geöffnet ist
    document.body.style.overflow = 'hidden';
  };
  const closeLightbox = () => {
    setIsLightboxOpen(false);
    // Scroll wieder aktivieren, wenn Lightbox geschlossen wird
    document.body.style.overflow = '';
  };
  // Cleanup-Funktion, um sicherzustellen, dass Scrolling wieder aktiviert wird
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  // Aspect ratio calculation for 16:9 (1920:1080)
  const aspectRatio = 16 / 9;
  return <div className="relative w-full h-full" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-144">
      <div className="relative w-full rounded-xl overflow-hidden shadow-xl border border-gray-200 bg-white" data-id="element-145">
        {/* Browser-like header */}
        <div className="h-8 bg-gray-100 border-b border-gray-200 flex items-center px-3" data-id="element-146">
          <div className="flex space-x-1.5" data-id="element-147">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-148"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" data-id="element-149"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-400" data-id="element-150"></div>
          </div>
          
        </div>
        {/* 16:9 container using aspect-ratio */}
        <div className="relative w-full aspect-video" style={{
        backgroundColor
      }} data-id="element-151">
          {/* Geometric corner accents */}
          <div className="absolute top-0 left-0 w-12 h-12 z-10" data-id="element-152">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-153">
              <path d="M0 0L48 0L0 48L0 0Z" fill="#9CC31C" fillOpacity="0.3" data-id="element-154" />
            </svg>
          </div>
          <div className="absolute bottom-0 right-0 w-12 h-12 z-10" data-id="element-155">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-156">
              <path d="M48 48L0 48L48 0L48 48Z" fill="#9CC31C" fillOpacity="0.3" data-id="element-157" />
            </svg>
          </div>
          <div className="flex justify-center items-center h-full overflow-hidden" data-id="element-158">
            <AnimatePresence mode="wait" data-id="element-159">
              {shouldShowSmartphoneFrame() ? <motion.div key={`smartphone-${currentIndex}`} className="relative bg-black rounded-3xl border-8 border-black shadow-xl" style={{
              maxWidth: '320px',
              maxHeight: '90%'
            }} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-160">
                  {/* Smartphone notch */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-6 bg-black rounded-b-xl z-20" data-id="element-161"></div>
                  {/* Smartphone image */}
                  <motion.img src={images[currentIndex]} alt={`${projectTitle} Screenshot ${currentIndex + 1}`} className="w-full rounded-xl cursor-pointer" onClick={openLightbox} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.5
              }} data-id="element-162" />
                  {/* Home indicator */}
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1/3 h-1 bg-gray-500 rounded-full" data-id="element-163"></div>
                </motion.div> : <motion.img key={currentIndex} src={images[currentIndex]} alt={`${projectTitle} Screenshot ${currentIndex + 1}`} className="w-full h-full object-contain px-4 cursor-pointer" onClick={openLightbox} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-164" />}
            </AnimatePresence>
          </div>
          {/* Animated navigation buttons */}
          <motion.button onClick={goToPrevious} className="absolute top-1/2 left-4 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition z-20" initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: isHovered ? 1 : 0,
          x: isHovered ? 0 : -10
        }} transition={{
          duration: 0.2
        }} data-id="element-165">
            <ChevronLeftIcon size={24} className="text-gray-800" data-id="element-166" />
          </motion.button>
          <motion.button onClick={goToNext} className="absolute top-1/2 right-4 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition z-20" initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: isHovered ? 1 : 0,
          x: isHovered ? 0 : 10
        }} transition={{
          duration: 0.2
        }} data-id="element-167">
            <ChevronRightIcon size={24} className="text-gray-800" data-id="element-168" />
          </motion.button>
        </div>
      </div>
      {/* Redesigned indicator dots */}
      <div className="flex justify-center mt-4 gap-2" data-id="element-169">
        {images.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className="group relative" data-id="element-170">
            <div className={`w-4 h-4 transition-all duration-300 transform ${index === currentIndex ? 'bg-[#9CC31C] scale-100' : 'bg-gray-300 scale-75 group-hover:scale-90'}`} style={{
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
        }} data-id="element-171" />
          </button>)}
      </div>
      {/* Lightbox */}
      <AnimatePresence data-id="element-172">
        {isLightboxOpen && <motion.div className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeLightbox} data-id="element-173">
            <motion.div className="relative max-w-5xl max-h-[90vh] w-full" initial={{
          scale: 0.9
        }} animate={{
          scale: 1
        }} exit={{
          scale: 0.9
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} onClick={e => e.stopPropagation()} data-id="element-174">
              <button onClick={closeLightbox} className="absolute -top-12 right-0 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition" data-id="element-175">
                <XIcon size={24} className="text-gray-800" data-id="element-176" />
              </button>
              {shouldShowSmartphoneFrame() ? <div className="relative bg-black rounded-3xl border-8 border-black shadow-xl mx-auto" style={{
            maxWidth: '480px'
          }} data-id="element-177">
                  {/* Smartphone notch */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-7 bg-black rounded-b-xl z-20" data-id="element-178"></div>
                  {/* Smartphone image */}
                  <img src={images[currentIndex]} alt={`Screenshot ${currentIndex + 1} (full size)`} className="w-full rounded-xl" data-id="element-179" />
                  {/* Home indicator */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-1/3 h-1.5 bg-gray-500 rounded-full" data-id="element-180"></div>
                </div> : <img src={images[currentIndex]} alt={`Screenshot ${currentIndex + 1} (full size)`} className="w-full h-full object-contain bg-white/5 rounded-lg shadow-2xl" data-id="element-181" />}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2" data-id="element-182">
                <button onClick={e => {
              e.stopPropagation();
              goToPrevious();
            }} className="bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition" data-id="element-183">
                  <ChevronLeftIcon size={24} className="text-gray-800" data-id="element-184" />
                </button>
                <button onClick={e => {
              e.stopPropagation();
              goToNext();
            }} className="bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition" data-id="element-185">
                  <ChevronRightIcon size={24} className="text-gray-800" data-id="element-186" />
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export default ImageCarousel;